import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { EntryPath } from '@/Config/app/router/entry.path';
import { AiAppListingButton } from '@/Domain/components/Auth/AiAppListingButton';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { ReCaptchaV3 } from '@/Domain/components/Auth/ReCaptchaV3';
import { GoogleButton } from '@/Domain/components/Auth/SocialButton/GoogleButton';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FieldError } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import { EnvelopIcon } from '@/Domain/components/icons/Envelop.icon';
import LockLineIcon from '@/Domain/components/icons/LockLineIcon';
import { SocialPlatformEnum } from '@/Domain/enum/SocialPlatform.enum';
import { useLogin } from '@/Domain/hooks/Auth/useLogin.hook';
import { useSocialLogin } from '@/Domain/hooks/Auth/useSocialLogin.hook';

export const LoginPage = () => {
  const { t } = useTranslation('login');
  const { search } = useLocation();

  const {
    useV2,
    control,
    escalateToV2,
    handleV2Focus,
    shouldFocusV2,
    register,
    handleSubmit,
    errors,
    isPending,
    onSubmit,
    onVerify,
    tooManyRequests,
    lockoutMinutes,
    showGoogleHint,
  } = useLogin();

  const { handleSocialLogin, isNavigating } = useSocialLogin();

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      <div className="grid gap-5">
        {/* Social Login */}
        <div className="grid gap-4">
          <GoogleButton
            onClick={(event) =>
              handleSocialLogin(event, SocialPlatformEnum.Google, '/login/google-oauth2/')
            }
            disabled={isNavigating}
            isLoading={isNavigating}
          />
          <AiAppListingButton app="chatgpt" source="login_page" />
          <AiAppListingButton app="claude" source="login_page" />
        </div>

        {/* Divider */}
        <div className="relative flex pt-1 items-center">
          <div className="flex-grow border-t border-border"></div>
          <span className="mx-4 flex-shrink text-xs font-medium uppercase tracking-wide text-foreground-subtle">
            {t('or')}
          </span>
          <div className="flex-grow border-t border-border"></div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <ReCaptchaV3 onVerify={onVerify} onUnavailable={escalateToV2} />

          {/* Email Field */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium text-foreground">
              {t('form.email')}
            </label>
            <Input
              id="email"
              size="lg"
              data-testid="login-email"
              {...register('username')}
              placeholder={t('form.email_placeholder')}
              error={!!errors.username}
              autoFocus
              autoComplete="email"
              startIcon={<EnvelopIcon className="h-4 w-4 text-foreground-subtle" />}
            />
            {errors.username && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.username.message}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div className="flex flex-col gap-1.5">
            {/* Input first in the DOM so Tab runs email -> password; `order` paints the label row above it. */}
            <div className="order-2">
              <PasswordInput
                id="password"
                size="lg"
                data-testid="login-password"
                placeholder={t('form.password_placeholder')}
                {...register('password')}
                error={!!errors.password}
                autoComplete="current-password"
                startIcon={<LockLineIcon className="h-4 w-4 text-foreground-subtle" />}
              />
            </div>
            <div className="order-1 flex items-center justify-between">
              <label htmlFor="password" className="text-sm font-medium text-foreground">
                {t('form.password')}
              </label>
              <Link
                to={`/${EntryPath.forgetPassword}${search}`}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover-underline"
              >
                {t('link.forgot_password')}
              </Link>
            </div>
            {errors.password && (
              <p className="order-3 text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Too Many Requests Error */}
          {tooManyRequests && (
            <div className="animate-in slide-in-from-top-2 rounded-lg border border-destructive-muted bg-destructive-muted p-3 duration-200 fade-in">
              <p
                className="text-sm font-medium text-destructive"
                data-testid="login-too-many-requests"
              >
                {lockoutMinutes === null
                  ? t('ERRORS:TOO_MANY_REQUESTS')
                  : t('locked_out', { minutes: lockoutMinutes })}
              </p>
            </div>
          )}

          {/* Google sign-in hint: this account was created with Google and has no password */}
          {showGoogleHint && (
            <div className="animate-in slide-in-from-top-2 grid gap-3 rounded-lg border border-info-muted bg-info-muted p-4 duration-200 fade-in">
              <p className="text-sm font-medium text-info">{t('google_hint')}</p>
              <GoogleButton
                onClick={(event) =>
                  handleSocialLogin(event, SocialPlatformEnum.Google, '/login/google-oauth2/')
                }
                disabled={isNavigating}
                isLoading={isNavigating}
              />
            </div>
          )}

          {errors.root && (
            <Alert
              variant="destructive"
              data-testid="login-error"
              className="animate-in fade-in duration-200"
            >
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          {/* reCAPTCHA v2 Fallback */}
          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox
                  onChange={onChange}
                  onFocusHandled={handleV2Focus}
                  value={value}
                  isInvalid={invalid}
                  shouldFocus={shouldFocusV2}
                />
              )}
            />
          )}
          {errors.recaptcha_response_v2 && (
            <FieldError className="text-center" data-testid="login-captcha-error">
              {t('validation:required')}
            </FieldError>
          )}

          {/* Submit Button */}
          <Button
            variant="dark"
            size="lg"
            type="submit"
            loading={isPending}
            className="w-full mt-1"
            data-testid="login-submit"
          >
            {t('button.login')}
          </Button>
        </form>

        {/* Register Link */}
        <div className="border-t border-border pt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('link.register_prompt')}{' '}
            <Link
              to={`${EntryPath.register}${search}`}
              className="font-medium text-foreground transition-colors hover-underline"
              data-testid="login-register-link"
            >
              {t('link.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
